import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, FileText, AlertCircle, Building2, Clock, Shield, ArrowLeft } from 'lucide-react';

const TermsAndConditionsAGB = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </button>
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-10 h-10" />
            <h1 className="text-4xl font-bold">Allgemeine Geschäftsbedingungen (AGB)</h1>
          </div>
          <p className="text-blue-100 text-lg">
            THG Premium Manager - Grid Balance UG
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Introduction */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">§ 1 Geltungsbereich und Vertragsgegenstand</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">1.1 Anwendungsbereich</h3>
              <p className="text-gray-700 leading-relaxed">
                Diese Allgemeinen Geschäftsbedingungen (nachfolgend "AGB") gelten für alle Geschäftsbeziehungen 
                zwischen der Grid Balance UG (nachfolgend "Anbieter") und ihren Kunden (nachfolgend "Kunde" oder "Autohaus") 
                im Rahmen der Nutzung des THG Premium Manager Systems zur Verwaltung und Abwicklung von THG-Quoten-Anträgen 
                für Elektrofahrzeuge.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">1.2 Vertragsgegenstand</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Anbieter stellt dem Kunden eine digitale Plattform zur Verfügung, über die Endkunden 
                THG-Quoten-Anträge für ihre Elektrofahrzeuge einreichen können. Der Anbieter übernimmt die 
                Prüfung, Verwaltung und Abwicklung dieser Anträge sowie die Kommunikation mit den zuständigen Behörden.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">1.3 Abweichende Bedingungen</h3>
              <p className="text-gray-700 leading-relaxed">
                Abweichende, entgegenstehende oder ergänzende AGB des Kunden werden nur dann und insoweit 
                Vertragsbestandteil, als der Anbieter ihrer Geltung ausdrücklich schriftlich zugestimmt hat.
              </p>
            </div>
          </div>
        </section>

        {/* Service Definition */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Building2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">§ 2 Leistungsumfang und Partnerschaften</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">2.1 Dienstleistungen des Anbieters</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Der Anbieter erbringt folgende Leistungen:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Bereitstellung einer individuellen Landing-Page für das Autohaus</li>
                <li>Entgegennahme und Prüfung von THG-Quoten-Anträgen</li>
                <li>Verwaltung der Kundendaten und Fahrzeuginformationen</li>
                <li>Kommunikation mit Behörden und Quotenhändlern</li>
                <li>Generierung und Verwaltung von Gutscheincodes</li>
                <li>Statusverfolgung und Benachrichtigungen für Endkunden</li>
                <li>Reporting und Statistiken für das Autohaus</li>
              </ul>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">2.2 Pflichten des Autohauses</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Das Autohaus verpflichtet sich:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Korrekte und vollständige Angaben bei der Registrierung zu machen</li>
                <li>Die bereitgestellte Landing-Page aktiv bei Kunden zu bewerben</li>
                <li>Endkunden über die Nutzung des THG Premium Manager Systems zu informieren</li>
                <li>Bei Änderungen der Kontaktdaten oder Geschäftsadresse unverzüglich zu informieren</li>
                <li>Die Zugangsdaten vertraulich zu behandeln und vor unbefugtem Zugriff zu schützen</li>
              </ul>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">2.3 Verfügbarkeit</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Anbieter bemüht sich um eine möglichst hohe Verfügbarkeit der Plattform. Eine Verfügbarkeit 
                von 100% kann technisch nicht gewährleistet werden. Wartungsarbeiten werden nach Möglichkeit 
                außerhalb der üblichen Geschäftszeiten durchgeführt und rechtzeitig angekündigt.
              </p>
            </div>
          </div>
        </section>

        {/* Application Processing */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">§ 3 Antragsbearbeitung und Dokumentenprüfung</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">3.1 Antragseinreichung</h3>
              <p className="text-gray-700 leading-relaxed">
                Endkunden reichen ihre THG-Quoten-Anträge über die Landing-Page des jeweiligen Autohauses ein. 
                Erforderlich sind vollständige Angaben zum Fahrzeug, zum Halter sowie die Vorlage des 
                Fahrzeugscheins (Zulassungsbescheinigung Teil I) in lesbarer Form.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">3.2 Dokumentenprüfung</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Anbieter prüft die eingereichten Dokumente auf Vollständigkeit, Lesbarkeit und Plausibilität. 
                Bei unvollständigen oder unleserlichen Unterlagen wird der Endkunde zur Nachreichung aufgefordert. 
                Der Anbieter behält sich vor, Anträge bei begründeten Zweifeln an der Echtheit der Dokumente abzulehnen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">3.3 Bearbeitungszeit</h3>
              <p className="text-gray-700 leading-relaxed">
                Die Bearbeitung erfolgt in der Regel innerhalb von 5-10 Werktagen nach Eingang vollständiger 
                Unterlagen. Die tatsächliche Bearbeitungszeit kann je nach Auslastung und Komplexität des 
                Einzelfalls variieren. Eine Garantie für bestimmte Bearbeitungszeiten wird nicht übernommen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">3.4 Ablehnungsgründe</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Anträge können aus folgenden Gründen abgelehnt werden:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Unvollständige oder unleserliche Dokumente</li>
                <li>Fahrzeug nicht als Elektrofahrzeug zugelassen</li>
                <li>Fahrzeug bereits für das laufende Quotenjahr registriert</li>
                <li>Zweifel an der Authentizität der eingereichten Dokumente</li>
                <li>Verstoß gegen gesetzliche Bestimmungen</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Voucher Codes */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Clock className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">§ 4 Gutscheincodes und Gültigkeit</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4.1 Generierung und Verteilung</h3>
              <p className="text-gray-700 leading-relaxed">
                Das Autohaus kann über die Plattform Gutscheincodes generieren und an Endkunden verteilen. 
                Diese Codes dienen der Zuordnung von Anträgen zum jeweiligen Autohaus und können mit 
                individuellen Konditionen oder Aktionen verknüpft werden.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4.2 Gültigkeitsdauer</h3>
              <p className="text-gray-700 leading-relaxed">
                Gutscheincodes sind standardmäßig 12 Monate ab Generierung gültig, sofern nicht anders 
                vereinbart. Abgelaufene Codes können nicht mehr verwendet werden. Das Autohaus wird 
                rechtzeitig über ablaufende Codes informiert.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4.3 Einlösung und Übertragbarkeit</h3>
              <p className="text-gray-700 leading-relaxed">
                Gutscheincodes sind nicht übertragbar und können nur einmal verwendet werden. Eine 
                Barauszahlung oder nachträgliche Änderung der mit dem Code verknüpften Konditionen ist 
                ausgeschlossen.
              </p>
            </div>
          </div>
        </section>

        {/* Data Protection */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Shield className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">§ 5 Datenschutz und Vertraulichkeit</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">5.1 Datenverarbeitung</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Anbieter verarbeitet personenbezogene Daten ausschließlich im Rahmen der gesetzlichen 
                Bestimmungen, insbesondere der Datenschutz-Grundverordnung (DSGVO). Detaillierte Informationen 
                zur Datenverarbeitung finden sich in der Datenschutzerklärung.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">5.2 Auftragsverarbeitung</h3>
              <p className="text-gray-700 leading-relaxed">
                Soweit der Anbieter personenbezogene Daten im Auftrag des Autohauses verarbeitet, erfolgt 
                dies auf Grundlage eines separaten Auftragsverarbeitungsvertrags gemäß Art. 28 DSGVO.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">5.3 Vertraulichkeit</h3>
              <p className="text-gray-700 leading-relaxed">
                Beide Vertragsparteien verpflichten sich, alle im Rahmen der Geschäftsbeziehung bekannt 
                gewordenen vertraulichen Informationen streng vertraulich zu behandeln und nur für die 
                Vertragserfüllung zu verwenden.
              </p>
            </div>
          </div>
        </section>

        {/* Liability */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <AlertCircle className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">§ 6 Haftung und Gewährleistung</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.1 Haftungsbeschränkung</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Anbieter haftet unbeschränkt für Schäden aus der Verletzung des Lebens, des Körpers oder 
                der Gesundheit sowie für Schäden, die auf einer vorsätzlichen oder grob fahrlässigen 
                Pflichtverletzung beruhen. Für leicht fahrlässige Pflichtverletzungen haftet der Anbieter nur 
                bei Verletzung wesentlicher Vertragspflichten (Kardinalpflichten).
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.2 Höhe der Haftung</h3>
              <p className="text-gray-700 leading-relaxed">
                Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten ist die Haftung auf den 
                vertragstypischen, vorhersehbaren Schaden begrenzt. Eine weitergehende Haftung ist ausgeschlossen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.3 Keine Garantie für Quotenhöhe</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Anbieter übernimmt keine Garantie für die Höhe der THG-Prämien oder die erfolgreiche 
                Vermarktung der Quoten. Die tatsächliche Prämienhöhe hängt von Marktbedingungen und behördlichen 
                Entscheidungen ab, auf die der Anbieter keinen Einfluss hat.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6.4 Höhere Gewalt</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Anbieter haftet nicht für Leistungsstörungen, die auf höherer Gewalt, Streik, Aussperrung 
                oder anderen unvorhersehbaren Ereignissen beruhen, die außerhalb seines Einflussbereichs liegen.
              </p>
            </div>
          </div>
        </section>

        {/* Contract Duration */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Clock className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">§ 7 Vertragslaufzeit und Kündigung</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7.1 Vertragsbeginn und Laufzeit</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Vertrag beginnt mit der erfolgreichen Registrierung des Autohauses und wird auf unbestimmte 
                Zeit geschlossen. Eine Mindestvertragslaufzeit besteht nicht.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7.2 Ordentliche Kündigung</h3>
              <p className="text-gray-700 leading-relaxed">
                Beide Vertragsparteien können den Vertrag jederzeit mit einer Frist von 4 Wochen zum Monatsende 
                ordentlich kündigen. Die Kündigung bedarf der Textform (z.B. E-Mail).
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7.3 Außerordentliche Kündigung</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt. Ein wichtiger 
                Grund liegt insbesondere vor bei:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Schwerwiegenden Verstößen gegen diese AGB</li>
                <li>Zahlungsverzug von mehr als 2 Monaten</li>
                <li>Insolvenz oder Liquidation einer Vertragspartei</li>
                <li>Missbrauch der Plattform oder betrügerischem Verhalten</li>
              </ul>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7.4 Folgen der Beendigung</h3>
              <p className="text-gray-700 leading-relaxed">
                Nach Vertragsende wird der Zugang zur Plattform gesperrt. Bereits eingereichte Anträge werden 
                noch zu Ende bearbeitet. Kundendaten werden gemäß den gesetzlichen Aufbewahrungsfristen gespeichert 
                und anschließend gelöscht.
              </p>
            </div>
          </div>
        </section>

        {/* Final Provisions */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Scale className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">§ 8 Schlussbestimmungen</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8.1 Änderungen der AGB</h3>
              <p className="text-gray-700 leading-relaxed">
                Der Anbieter behält sich vor, diese AGB zu ändern. Änderungen werden dem Kunden mindestens 
                4 Wochen vor ihrem Inkrafttreten per E-Mail mitgeteilt. Widerspricht der Kunde nicht innerhalb 
                von 4 Wochen nach Zugang der Änderungsmitteilung, gelten die geänderten AGB als angenommen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8.2 Anwendbares Recht</h3>
              <p className="text-gray-700 leading-relaxed">
                Es gilt ausschließlich das Recht der Bundesrepublik Deutschland unter Ausschluss des 
                UN-Kaufrechts (CISG).
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8.3 Gerichtsstand</h3>
              <p className="text-gray-700 leading-relaxed">
                Ist der Kunde Kaufmann, juristische Person des öffentlichen Rechts oder öffentlich-rechtliches 
                Sondervermögen, ist ausschließlicher Gerichtsstand für alle Streitigkeiten aus diesem Vertrag 
                der Sitz des Anbieters.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8.4 Salvatorische Klausel</h3>
              <p className="text-gray-700 leading-relaxed">
                Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit 
                der übrigen Bestimmungen hiervon unberührt. Die unwirksame Bestimmung ist durch eine wirksame 
                zu ersetzen, die dem wirtschaftlichen Zweck der unwirksamen Bestimmung am nächsten kommt.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8.5 Schriftform</h3>
              <p className="text-gray-700 leading-relaxed">
                Änderungen und Ergänzungen dieser AGB bedürfen der Schriftform. Dies gilt auch für die 
                Aufhebung des Schriftformerfordernisses.
              </p>
            </div>
          </div>
        </section>

        {/* Version Info */}
        <section className="bg-blue-50 rounded-xl p-8 border border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Versionsinformationen</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            <strong>Stand:</strong> Januar 2026<br />
            <strong>Version:</strong> 1.0<br />
            <strong>Gültig ab:</strong> 01.01.2026
          </p>
        </section>

      </div>
    </div>
  );
};

export default TermsAndConditionsAGB;